import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Plus, Trash2, CornerUpLeft, CornerUpRight, MoveRight, MoveLeft, Check, Loader2,
    ArrowLeft, ArrowRight, RotateCcw, Target, Pencil,
} from 'lucide-react';
import { toast, ConfirmationModal } from '../V2Components';
import UserFloorPlanService from '../../../services/UserFloorPlanService';
import {
    buildGhostOpeningSurface,
    buildGhostWallSurface,
    buildAnchorCornerMarkers,
    buildOriginMarker,
    buildResizePinMarker,
    buildSavePayload,
    buildViewerPayload,
    cascadeDeleteFixedSurface,
    deriveSectionSurfaces,
    fixedSurfaceFitWarning,
    fixedWallEndpoints,
    fixedWindowSill,
    headingForAnchorPreset,
    newUuid,
    normalizeTurnDeg,
    parsePlanToEditorState,
    resizeFixedSurface,
    resizeFixedWindowSill,
    DEFAULT_WALL_HEIGHT_M,
    HEIGHT_OVERRIDE_EPSILON_M,
} from '../../../utils/floorPlanGeometry';
import {
    convertMetersValue,
    getUnitSystemSuffix,
    formatLinearMeasurement,
    formatArea,
} from '../../../utils/measurementUtils';
import { AXIS_COLORS } from '../../../utils/sceneUtils';

const OPENING_TYPES = [
    { value: 'door', label: 'Door', defaultWidthM: 0.91, defaultHeightM: 2.03 },
    { value: 'window', label: 'Window', defaultWidthM: 1.2, defaultHeightM: 1.2 },
    { value: 'opening', label: 'Opening', defaultWidthM: 1.2, defaultHeightM: 2.03 },
];

const DEFAULT_WINDOW_SILL_M = 0.9;

// Server bounds the editor must respect so a previewable edit is always
// saveable (schemas/user_floor_plan.py: walk_turn_deg ∈ [-360, 360],
// wall_height_m ∈ (0, 15]). Mirrored here as the single source of the
// client-side limits.
const MAX_TURN_DEG = 360;
const MAX_WALL_HEIGHT_M = 15;

// Starting value for the free-form nudge-step field, in the user's display
// unit (the field itself is unit-relative: "0.5" means half an inch for an
// inches user). Any positive decimal works once the user types their own.
const DEFAULT_NUDGE_STEP_DISPLAY = {
    meters: '0.1',
    feet: '0.5',
    inches: '1',
};

// One nudge row per world axis: [−] letter+value [+]. Arrows are tinted
// with their axis color (AXIS_COLORS, shared with the viewer's gizmo) —
// screen directions drift as the camera orbits, so "press the cyan arrow
// to move along the cyan line" is the pairing that stays legible from any
// angle. X/Z translate the walk in the floor plane; Y lifts the whole run
// off the ground (ledges above half walls, raised sections). For
// reference: the top camera maps +X to screen right and +Z to screen down.
const NUDGE_AXES = [
    { axis: 'x', color: AXIS_COLORS.x, lineName: 'orange X line' },
    { axis: 'y', color: AXIS_COLORS.y, lineName: 'green Y line (elevation)' },
    { axis: 'z', color: AXIS_COLORS.z, lineName: 'cyan Z line' },
];

// Heading presets for a walk anchored to a scanned wall, relative to that
// wall's own direction (see headingForAnchorPreset).
const ANCHOR_PRESETS = [
    { value: 'continue', label: 'Continue', ariaLabel: 'Heading: continue along the wall', icon: MoveRight },
    { value: 'reverse', label: 'Back', ariaLabel: 'Heading: back along the wall', icon: MoveLeft },
    { value: 'left', label: 'Left', ariaLabel: 'Heading: perpendicular left', icon: CornerUpLeft },
    { value: 'right', label: 'Right', ariaLabel: 'Heading: perpendicular right', icon: CornerUpRight },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Direction glyph for a turn/rotation value — shared by the walk-start
// rotation indicator and the per-wall row indicators.
const turnArrow = (deg) => (deg > 0 ? '↱' : deg < 0 ? '↰' : '→');

const inputClass = 'w-full px-2.5 py-1.5 rounded-lg text-sm bg-[var(--bg-hover)] text-[var(--text-main)] border border-[var(--border)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]';
const labelClass = 'text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wide';

/**
 * Chained perimeter-walk editor for one user floor plan.
 *
 * Lives in the right column of RoomDetails' floor-plan mode (where
 * SceneObjectList sits when not editing). Every edit re-derives the full
 * surface set client-side and pushes it up via onPreviewChange, so the
 * 3D viewer is a live preview of the walk.
 *
 * @param {Object} props
 * @param {Object} props.plan - Full plan response (GET /user-floor-plans/:id)
 * @param {string} props.unitSystem - 'meters' | 'feet' | 'inches'
 * @param {Function} props.onPreviewChange - (viewerPayload) => void
 * @param {Function} props.onSaved - (updatedPlan) => void
 * @param {Function} props.onCancel
 * @param {string|null} [props.selectedId] - Surface picked in the 3D viewer;
 *   a scan-imported wall in the active section surfaces the anchor card.
 * @param {Function} [props.onSelect] - (identifier|null) => void — used to
 *   clear the viewer pick once an anchor is applied.
 */
const FloorPlanEditorPanel = ({ plan, unitSystem, onPreviewChange, onSaved, onCancel, selectedId, onSelect }) => {
    const [sections, setSections] = useState(() => parsePlanToEditorState(plan));
    const [activeSectionIdx, setActiveSectionIdx] = useState(0);
    const [planName, setPlanName] = useState(plan?.name || 'Floor Plan');
    const [wallForm, setWallForm] = useState({ length: '', turn: 0 });
    const [openingForm, setOpeningForm] = useState({
        wallUuid: '', type: 'door', offset: '', width: '', height: '', sill: '',
    });
    const [openingError, setOpeningError] = useState(null);
    const [saving, setSaving] = useState(false);
    // While the wall-height field is being edited, mirror the raw string so
    // partial input ("2.") isn't reformatted out from under the user. Null
    // means "not editing — show the value derived from section state".
    const [wallHeightInput, setWallHeightInput] = useState(null);
    // Same raw-string mirror for the inline wall-row inputs. Only one cell
    // can be focused at a time: { uuid, field: 'length'|'turn', value }.
    const [editingCell, setEditingCell] = useState(null);
    // Raw-string mirror for the walk-start X/Y/Z + rotation inputs (same
    // treatment as wallHeightInput): { field: 'x'|'y'|'z'|'rot', value }
    // while focused, else null.
    const [positionInput, setPositionInput] = useState(null);
    // How far one arrow press moves, as typed in the user's display unit.
    // Free-form so any decimal (half an inch, 2.5 ft, …) is a valid step.
    const [nudgeStepInput, setNudgeStepInput] = useState(
        () => DEFAULT_NUDGE_STEP_DISPLAY[unitSystem] || DEFAULT_NUDGE_STEP_DISPLAY.meters,
    );

    // The step is typed in the DISPLAY unit — when the viewer's m/ft/in
    // toggle changes while the panel stays mounted, re-seed to the new
    // unit's default instead of silently reinterpreting the same digits
    // as a different distance ("6" inches becoming 6 feet).
    useEffect(() => {
        setNudgeStepInput(DEFAULT_NUDGE_STEP_DISPLAY[unitSystem] || DEFAULT_NUDGE_STEP_DISPLAY.meters);
    }, [unitSystem]);
    // Anchor-card choice, keyed by the selection it was made for so a new
    // wall pick re-derives fresh defaults without an effect: null or
    // { forId, end: 'endA'|'endB', preset }.
    const [anchorChoice, setAnchorChoice] = useState(null);
    // Raw-string mirror for the fixed-surface edit card's numeric inputs,
    // keyed to the selection it was typed for so switching surfaces can't
    // show another surface's half-typed value:
    // { forId, field: 'width'|'height'|'depth'|'sill', value } | null.
    const [fixedEditInput, setFixedEditInput] = useState(null);
    // Which end of a fixed wall stays pinned while its width is edited.
    // Keyed like anchorChoice; a fresh pick falls back to 'center'
    // (Revit's typed-dimension behavior — end-pinning is the grip-stretch
    // model, opted into explicitly): { forId, anchor } | null.
    const [resizeAnchorChoice, setResizeAnchorChoice] = useState(null);
    // Imported surface awaiting delete confirmation. Every imported-
    // surface delete confirms (destructive-action guideline) — unlike
    // parametric rows, these are captured scan data, and the only undo
    // is cancelling the whole editing session:
    // { identifier, surfaceType, childCount } | null.
    const [fixedSurfaceToDelete, setFixedSurfaceToDelete] = useState(null);

    // Display-unit conversion. All editor math is meters; inputs read and
    // write the user's chosen unit.
    const unitFactor = convertMetersValue(1, unitSystem);
    const suffix = getUnitSystemSuffix(unitSystem);
    const toMeters = useCallback((value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n / unitFactor : NaN;
    }, [unitFactor]);

    const activeSection = sections[activeSectionIdx] || null;
    const derived = useMemo(
        () => (activeSection ? deriveSectionSurfaces(activeSection) : null),
        [activeSection],
    );

    // Ghost of the wall the add-form WOULD create — rendered translucent in
    // the viewer so the user sees where it lands before clicking Add. The
    // memo doubles as the preview-effect guard: while the length field is
    // empty/invalid this stays null (same reference), so fiddling with the
    // turn input alone doesn't re-fire the preview. Mirrors addWall's
    // frame conversion: an empty section's form rotation is relative to
    // the starter frame.
    const wallGhost = useMemo(() => {
        if (!activeSection) return null;
        const lengthM = toMeters(wallForm.length);
        if (!Number.isFinite(lengthM) || lengthM <= 0) return null;
        const formTurn = Number(wallForm.turn) || 0;
        const turnDeg = activeSection.walls.length === 0
            ? normalizeTurnDeg((Number(activeSection.frameRotationDeg) || 0) + formTurn)
            : formTurn;
        return buildGhostWallSurface(activeSection, { lengthM, turnDeg });
    }, [activeSection, wallForm, toMeters]);

    // Same treatment for the opening form: once a wall is picked and the
    // size is valid, preview the door/window/opening on its wall before
    // Add. Tracks every form field live (offset slides it along the wall,
    // sill lifts a window). Null while the form is incomplete.
    const openingGhost = useMemo(() => {
        if (!activeSection || !openingForm.wallUuid) return null;
        const widthM = toMeters(openingForm.width);
        const heightM = toMeters(openingForm.height);
        if (!Number.isFinite(widthM) || widthM <= 0 || !Number.isFinite(heightM) || heightM <= 0) return null;
        const offsetM = toMeters(openingForm.offset || 0);
        const sillM = openingForm.type === 'window' ? toMeters(openingForm.sill || 0) : 0;
        return buildGhostOpeningSurface(activeSection, {
            type: openingForm.type,
            wallUuid: openingForm.wallUuid,
            offsetM: Number.isFinite(offsetM) ? offsetM : 0,
            widthM,
            heightM,
            sillM: Number.isFinite(sillM) ? sillM : 0,
        });
    }, [activeSection, openingForm, toMeters]);

    const updateActiveSection = useCallback((updater) => {
        setSections((prev) => prev.map((section, idx) => (
            idx === activeSectionIdx ? updater(section) : section
        )));
    }, [activeSectionIdx]);

    // ------------------------------------------------------------------
    // Walk start (whole-run position)
    // ------------------------------------------------------------------
    // Translating section.start moves the entire chained walk — every wall
    // AND its openings re-derive from the new origin, so the run moves as
    // one rigid piece. Y edits section.startY, lifting the run (and its
    // auto floor) off the ground. Persistence is free either way: position
    // and elevation are baked into the walls' saved transforms and
    // recovered on the next parse.
    const nudge = (axis, direction) => {
        // Invalid/empty/non-positive step → arrows do nothing rather than
        // silently moving by a stale or nonsensical amount.
        const stepM = toMeters(nudgeStepInput);
        if (!Number.isFinite(stepM) || stepM <= 0) return;
        updateActiveSection((section) => {
            if (axis === 'y') {
                return { ...section, startY: (Number(section.startY) || 0) + stepM * direction };
            }
            // X/Z arrows move along the STARTER FRAME's axes, not raw world
            // axes — once the frame is rotated to match the room (or a
            // scanned wall), nudges slide along/into the room's own
            // directions, exactly like the gizmo lines show. Frame Z is
            // frame X + 90° (clockwise-from-above, the walk convention).
            const frameDeg = Number(section.frameRotationDeg) || 0;
            const angleRad = ((axis === 'x' ? frameDeg : frameDeg + 90) * Math.PI) / 180;
            const [x, z] = section.start || [0, 0];
            return {
                ...section,
                start: [
                    x + stepM * direction * Math.cos(angleRad),
                    z + stepM * direction * Math.sin(angleRad),
                ],
            };
        });
    };

    const positionValue = (axis) => {
        if (positionInput?.field === axis) return positionInput.value;
        const meters = axis === 'y'
            ? (Number(activeSection.startY) || 0)
            : (activeSection.start || [0, 0])[axis === 'x' ? 0 : 1];
        return Number((meters * unitFactor).toFixed(2));
    };

    const setStartAxis = (axis, rawValue) => {
        setPositionInput({ field: axis, value: rawValue });
        // Guard the empty string: Number('') === 0 would silently snap the
        // axis to zero while the user clears the field (same guard as the
        // inline turn cell).
        if (String(rawValue).trim() === '') return;
        const meters = toMeters(rawValue);
        if (!Number.isFinite(meters)) return;
        updateActiveSection((section) => {
            if (axis === 'y') return { ...section, startY: meters };
            const [x, z] = section.start || [0, 0];
            return { ...section, start: axis === 'x' ? [meters, z] : [x, meters] };
        });
    };

    // Starter-frame rotation. Spins ONLY the starter: the gizmo's X/Z
    // lines, the nudge directions, and the reference for the FIRST wall's
    // heading (added later as frame + form rotation). Existing walls keep
    // their absolute headings and never move — rotate the frame to match a
    // room corner, then build in the room's own directions.
    const frameRotationDeg = Number(activeSection?.frameRotationDeg) || 0;

    const runRotationValue = () => {
        if (positionInput?.field === 'rot') return positionInput.value;
        return Number(frameRotationDeg.toFixed(2));
    };

    const setRunRotation = (rawValue) => {
        setPositionInput({ field: 'rot', value: rawValue });
        // Same empty-string guard as the turn cells: Number('') === 0 would
        // zero the rotation while the user clears the field.
        if (String(rawValue).trim() === '') return;
        const deg = Number(rawValue);
        if (!Number.isFinite(deg)) return;
        const clamped = clamp(deg, -MAX_TURN_DEG, MAX_TURN_DEG);
        updateActiveSection((section) => ({ ...section, frameRotationDeg: clamped }));
    };

    // ------------------------------------------------------------------
    // Anchor to a scanned wall
    // ------------------------------------------------------------------
    // The viewer pick only anchors against scan-imported walls of the
    // ACTIVE section — parametric walls are already editable in place, and
    // a wall in another section is an ambiguous target (switch sections
    // first). Reads the fixed surface's geometry; never mutates it.
    const anchorTarget = useMemo(() => {
        if (!selectedId || !activeSection) return null;
        const fixedWall = (activeSection.fixedSurfaces || []).find(
            (f) => f.surface_type === 'wall' && f.viewerDict?.identifier === selectedId,
        );
        if (!fixedWall) return null;
        const endpoints = fixedWallEndpoints(fixedWall.viewerDict);
        if (!endpoints) return null;
        return { viewerDict: fixedWall.viewerDict, ...endpoints };
    }, [selectedId, activeSection]);

    // "Near/Far end" is measured from where the walk currently stands: the
    // last wall's end, or the walk origin for an empty section.
    const anchorReference = useMemo(() => {
        if (derived?.wallInfo?.length) return derived.wallInfo[derived.wallInfo.length - 1].end;
        return activeSection?.start || [0, 0];
    }, [derived, activeSection]);

    const nearEndKey = useMemo(() => {
        if (!anchorTarget) return 'endA';
        const dist = (p) => Math.hypot(p[0] - anchorReference[0], p[1] - anchorReference[1]);
        return dist(anchorTarget.endA) <= dist(anchorTarget.endB) ? 'endA' : 'endB';
    }, [anchorTarget, anchorReference]);

    // The stored choice only applies to the selection it was made for; any
    // new pick falls back to fresh defaults (near end, continue). Memoized
    // because the anchor-corner ghost memo (and through it the preview
    // effect) depends on its identity — a fresh object every render would
    // re-fire the preview in a loop.
    const effectiveAnchorChoice = useMemo(() => (
        (anchorChoice && anchorChoice.forId === selectedId)
            ? anchorChoice
            : { forId: selectedId, end: nearEndKey, preset: 'continue' }
    ), [anchorChoice, selectedId, nearEndKey]);

    const applyAnchor = () => {
        if (!anchorTarget) return;
        const endpoint = anchorTarget[effectiveAnchorChoice.end];
        // Land at the scanned wall's base elevation too — anchoring to an
        // upstairs/raised wall should not drop the new run to the ground.
        // Scan-noise elevations within rounding of 0 snap to exactly 0.
        const anchorY = Math.abs(anchorTarget.baseY || 0) > HEIGHT_OVERRIDE_EPSILON_M
            ? anchorTarget.baseY
            : 0;
        // Orient the starter frame to the wall: X ⟂ wall on its scanned-
        // normal side, Z along it. From here the frame — not the world —
        // is the reference for nudges and the first wall's heading.
        const anchorFrame = Number(anchorTarget.normalHeadingDeg.toFixed(2));
        updateActiveSection((section) => ({
            ...section,
            start: [...endpoint],
            startY: anchorY,
            frameRotationDeg: anchorFrame,
        }));
        if (activeSection.walls.length === 0) {
            // Pre-fill the Add Wall rotation with the chosen preset,
            // expressed relative to the wall-aligned frame — Continue/Back
            // come out ±90 (along the wall), Left/Right 0/180 (⟂ to it),
            // so "Straight along the wall" is finally a literal value.
            const prefill = normalizeTurnDeg(headingForAnchorPreset(
                anchorTarget.headingDeg,
                effectiveAnchorChoice.end,
                effectiveAnchorChoice.preset,
            ) - anchorFrame);
            setWallForm((f) => ({ ...f, turn: Number(prefill.toFixed(2)) }));
        }
        // The pick is consumed — clear it so the card dismisses and the
        // origin marker shows the applied start.
        onSelect?.(null);
        setAnchorChoice(null);
    };

    // Corner previews for the selected scanned wall: the chosen end shows
    // the exact start point + wall-aligned FRAME "Use as start point" will
    // apply (its gizmo previews Z along the wall, X ⟂ to it); the other
    // end is a subdued candidate.
    const anchorCornerGhosts = useMemo(() => {
        if (!anchorTarget || !activeSection) return [];
        return buildAnchorCornerMarkers(anchorTarget, {
            sectionUuid: activeSection.sectionUuid,
            chosenEnd: effectiveAnchorChoice.end,
            headingDeg: anchorTarget.normalHeadingDeg,
        });
    }, [anchorTarget, activeSection, effectiveAnchorChoice]);

    // ------------------------------------------------------------------
    // Fixed (scan-imported) surface editing
    // ------------------------------------------------------------------
    // The selected fixed surface of the ACTIVE section — derived from the
    // same viewer pick as anchorTarget (both cards must never disagree
    // about what's selected). Floors are excluded: their outline is a
    // many-cornered polygon a W×H card can't meaningfully resize. NOTE:
    // this is a fresh object reference on every fixedSurfaces change (each
    // resize keystroke) — use selectedId, never this identity, to mean
    // "the same surface is still selected".
    const editableFixedSurface = useMemo(() => {
        if (!selectedId || !activeSection) return null;
        return (activeSection.fixedSurfaces || []).find(
            (f) => f.surface_type !== 'floor' && f.viewerDict?.identifier === selectedId,
        ) || null;
    }, [selectedId, activeSection]);

    // Parent wall of a selected fixed door/window/opening — the sill math
    // needs its base elevation. Null for walls and orphaned children.
    const editableFixedParentWall = useMemo(() => {
        if (!editableFixedSurface || editableFixedSurface.surface_type === 'wall') return null;
        const parentId = editableFixedSurface.viewerDict?.parentIdentifier;
        return (activeSection.fixedSurfaces || []).find(
            (f) => f.surface_type === 'wall' && f.viewerDict?.identifier === parentId,
        ) || null;
    }, [editableFixedSurface, activeSection]);

    // Children of the selected fixed WALL — drives both the delete-
    // confirmation count and the live fit warnings.
    const editableFixedWallChildren = useMemo(() => {
        if (!editableFixedSurface || editableFixedSurface.surface_type !== 'wall') return [];
        return cascadeDeleteFixedSurface(
            activeSection.fixedSurfaces,
            editableFixedSurface.viewerDict.identifier,
        ).map((f) => f.viewerDict);
    }, [editableFixedSurface, activeSection]);

    // Children keep their own world transforms when the wall resizes, so
    // a shrink can strand one outside the wall — warn, don't clamp (same
    // contract as the parametric openingFitWarnings).
    const fixedFitWarnings = useMemo(() => {
        if (!editableFixedSurface || editableFixedSurface.surface_type !== 'wall') return [];
        return fixedSurfaceFitWarning(editableFixedSurface.viewerDict, editableFixedWallChildren);
    }, [editableFixedSurface, editableFixedWallChildren]);

    const effectiveResizeAnchor = (resizeAnchorChoice && resizeAnchorChoice.forId === selectedId)
        ? resizeAnchorChoice.anchor
        : 'center';

    // Lime pillar at the pinned end while an end anchor is active — the
    // anchor card's fuchsia corner stubs can sit on the same corners at
    // the same time, so the pin gets its own shape and color.
    const resizePinGhost = useMemo(() => {
        if (!editableFixedSurface || editableFixedSurface.surface_type !== 'wall') return null;
        if (effectiveResizeAnchor === 'center') return null;
        const endpoints = fixedWallEndpoints(editableFixedSurface.viewerDict);
        if (!endpoints) return null;
        return buildResizePinMarker(endpoints, {
            sectionUuid: activeSection.sectionUuid,
            pinnedEnd: effectiveResizeAnchor,
        });
    }, [editableFixedSurface, effectiveResizeAnchor, activeSection]);

    // Commit a resize into the stored viewerDict. resizeFixedSurface
    // returns null on anything malformed — treat that as a no-op instead
    // of corrupting the entry.
    const updateFixedSurface = useCallback((identifier, patch) => {
        updateActiveSection((section) => ({
            ...section,
            fixedSurfaces: section.fixedSurfaces.map((f) => {
                if (f.viewerDict?.identifier !== identifier) return f;
                const resized = resizeFixedSurface(f.viewerDict, patch);
                return resized ? { ...f, viewerDict: resized } : f;
            }),
        }));
    }, [updateActiveSection]);

    // Sill is a translate-only edit that needs the parent wall's base —
    // both looked up inside the updater so they come from the same state.
    const updateFixedWindowSill = useCallback((identifier, sillM) => {
        updateActiveSection((section) => ({
            ...section,
            fixedSurfaces: section.fixedSurfaces.map((f) => {
                if (f.viewerDict?.identifier !== identifier) return f;
                const parent = section.fixedSurfaces.find(
                    (p) => p.surface_type === 'wall' && p.viewerDict?.identifier === f.viewerDict?.parentIdentifier,
                );
                if (!parent) return f;
                const moved = resizeFixedWindowSill(f.viewerDict, parent.viewerDict, sillM);
                return moved ? { ...f, viewerDict: moved } : f;
            }),
        }));
    }, [updateActiveSection]);

    // Removal cascades to the wall's children in the SAME state update so
    // no intermediate render carries orphaned doors/windows. All of this
    // is editor state — Cancel still discards everything unsaved.
    const removeFixedSurfaceNow = (identifier) => {
        const childIds = cascadeDeleteFixedSurface(activeSection?.fixedSurfaces, identifier)
            .map((f) => f.viewerDict.identifier);
        updateActiveSection((section) => ({
            ...section,
            fixedSurfaces: section.fixedSurfaces.filter((f) => (
                f.viewerDict?.identifier !== identifier
                && f.viewerDict?.parentIdentifier !== identifier
            )),
        }));
        // Don't leave the edit card pointing at deleted geometry.
        if (selectedId === identifier || childIds.includes(selectedId)) {
            onSelect?.(null);
        }
    };

    // Every imported-surface delete confirms; walls additionally surface
    // their exact cascade count so the modal can say what goes with them.
    const requestDeleteFixedSurface = (fixedEntry) => {
        const children = fixedEntry.surface_type === 'wall'
            ? cascadeDeleteFixedSurface(activeSection.fixedSurfaces, fixedEntry.viewerDict.identifier)
            : [];
        setFixedSurfaceToDelete({
            identifier: fixedEntry.viewerDict.identifier,
            surfaceType: fixedEntry.surface_type,
            childCount: children.length,
        });
    };

    const confirmDeleteFixedSurface = () => {
        if (!fixedSurfaceToDelete) return;
        removeFixedSurfaceNow(fixedSurfaceToDelete.identifier);
        setFixedSurfaceToDelete(null);
    };

    // Display value for an edit-card field: the raw mirror while the user
    // types in THIS surface's field, else the value derived from state
    // (same rounding as the other editor inputs). Depth shows blank when
    // unset — 0 means "assumed standard", not a measured zero.
    const fixedFieldValue = (field) => {
        if (fixedEditInput?.forId === selectedId && fixedEditInput.field === field) {
            return fixedEditInput.value;
        }
        const dims = editableFixedSurface?.viewerDict?.dimensions;
        if (field === 'width') return Number(((Number(dims?.[0]) || 0) * unitFactor).toFixed(2));
        if (field === 'height') return Number(((Number(dims?.[1]) || 0) * unitFactor).toFixed(2));
        if (field === 'depth') {
            const depth = Number(dims?.[2]);
            return Number.isFinite(depth) && depth > 0 ? Number((depth * unitFactor).toFixed(2)) : '';
        }
        if (field === 'sill') {
            const sill = editableFixedParentWall
                ? fixedWindowSill(editableFixedSurface?.viewerDict, editableFixedParentWall.viewerDict)
                : null;
            return sill == null ? '' : Number((sill * unitFactor).toFixed(2));
        }
        return '';
    };

    const handleFixedFieldChange = (field, rawValue) => {
        setFixedEditInput({ forId: selectedId, field, value: rawValue });
        const identifier = editableFixedSurface?.viewerDict?.identifier;
        if (!identifier) return;
        if (String(rawValue).trim() === '') {
            // Clearing depth is a real action — reset to "assumed
            // standard". Clearing width/height/sill just pauses the edit.
            if (field === 'depth') updateFixedSurface(identifier, { depthM: 0 });
            return;
        }
        const meters = toMeters(rawValue);
        if (!Number.isFinite(meters)) return;
        if (field === 'width' && meters > 0) {
            updateFixedSurface(identifier, { widthM: meters, anchor: effectiveResizeAnchor });
        } else if (field === 'height' && meters > 0) {
            updateFixedSurface(identifier, { heightM: meters });
        } else if (field === 'depth' && meters > 0) {
            updateFixedSurface(identifier, { depthM: meters });
        } else if (field === 'sill' && meters >= 0) {
            updateFixedWindowSill(identifier, meters);
        }
    };

    // Marker at the active section's walk origin — always visible while
    // editing so nudges give immediate feedback and an anchored start is
    // visible before the first wall exists. Carries the starter frame
    // rotation so the viewer's gizmo axes spin with the rotation field;
    // hands the gizmo off to the chosen-corner preview while anchoring.
    // Memoized on activeSection so switching sections moves the marker
    // even though `sections` itself doesn't change identity.
    const originMarker = useMemo(
        () => buildOriginMarker(activeSection, { headingDeg: frameRotationDeg, gizmo: !anchorTarget }),
        [activeSection, frameRotationDeg, anchorTarget],
    );

    // Live preview: any state change re-derives every section and hands the
    // viewer payload up. Identity changes per edit, which is exactly what
    // the viewer's "fire once per scanData identity" effect wants.
    useEffect(() => {
        if (onPreviewChange) {
            onPreviewChange(buildViewerPayload(sections, {
                ghosts: [wallGhost, openingGhost, originMarker, resizePinGhost, ...anchorCornerGhosts],
            }));
        }
    }, [sections, wallGhost, openingGhost, originMarker, resizePinGhost, anchorCornerGhosts, onPreviewChange]);

    // ------------------------------------------------------------------
    // Walls
    // ------------------------------------------------------------------
    const addWall = () => {
        const lengthM = toMeters(wallForm.length);
        if (!Number.isFinite(lengthM) || lengthM <= 0) {
            toast.error('Enter a wall length greater than zero');
            return;
        }
        const formTurn = clamp(Number(wallForm.turn) || 0, -MAX_TURN_DEG, MAX_TURN_DEG);
        // The FIRST wall's form rotation is relative to the starter frame
        // (0 = along frame X); it's stored as the absolute heading so the
        // saved plan stays frame-independent. Later walls keep their turn
        // relative to the previous wall, as typed.
        const turnDeg = activeSection.walls.length === 0
            ? normalizeTurnDeg(frameRotationDeg + formTurn)
            : formTurn;
        updateActiveSection((section) => ({
            ...section,
            walls: [...section.walls, { uuid: newUuid(), lengthM, turnDeg }],
        }));
        // Perimeter walks are usually rectangular: after the first wall,
        // default the next turn to a right angle.
        setWallForm({ length: '', turn: 90 });
    };

    // Patch one wall in place. Geometry derives from the wall list, so
    // everything downstream of the edited wall re-chains automatically.
    const updateWall = useCallback((uuid, patch) => {
        updateActiveSection((section) => ({
            ...section,
            walls: section.walls.map((w) => (w.uuid === uuid ? { ...w, ...patch } : w)),
        }));
    }, [updateActiveSection]);

    const removeWall = (uuid) => {
        setEditingCell((cell) => (cell?.uuid === uuid ? null : cell));
        updateActiveSection((section) => ({
            ...section,
            walls: section.walls.filter((w) => w.uuid !== uuid),
            // Openings live on their wall — they go with it.
            openings: section.openings.filter((o) => o.wallUuid !== uuid),
        }));
    };

    // Inline-row display value: the raw mirror while this cell is focused,
    // otherwise the formatted value from section state (same rounding as
    // the wall-height field). The height cell shows the EFFECTIVE height —
    // the wall's override when set, else the section default.
    const wallCellValue = (wall, field) => {
        if (editingCell?.uuid === wall.uuid && editingCell.field === field) return editingCell.value;
        if (field === 'length') return Number((wall.lengthM * unitFactor).toFixed(2));
        if (field === 'height') {
            const meters = Number(wall.heightM) > 0 ? Number(wall.heightM) : activeSection.wallHeightM;
            return Number((meters * unitFactor).toFixed(2));
        }
        return Number(wall.turnDeg.toFixed(2));
    };

    // Walls can shrink after openings were placed on them — flag any opening
    // that no longer fits its wall (offset + width past the wall's end).
    // Non-blocking: the opening still renders (overhanging the end), and the
    // user decides whether to lengthen the wall or move the opening.
    const openingFitWarnings = useMemo(() => {
        if (!derived || !activeSection) return [];
        const wallByUuid = new Map(derived.wallInfo.map((w) => [w.uuid, w]));
        return activeSection.openings
            .map((opening) => ({
                opening,
                wall: wallByUuid.get(opening.wallUuid),
                wallIdx: activeSection.walls.findIndex((w) => w.uuid === opening.wallUuid),
            }))
            .filter(({ opening, wall }) => wall && opening.offsetM + opening.widthM > wall.lengthM + 1e-6);
    }, [derived, activeSection]);

    // Append the segment that walks from the current endpoint back to the
    // walk origin, closing the loop in one click.
    const closeLoop = () => {
        if (!derived || derived.wallInfo.length < 2 || derived.isClosed) return;
        const last = derived.wallInfo[derived.wallInfo.length - 1];
        const [sx, sz] = derived.wallInfo[0].start;
        const dx = sx - last.end[0];
        const dz = sz - last.end[1];
        const length = Math.hypot(dx, dz);
        if (length <= 0) return;
        const headingNeeded = (Math.atan2(dz, dx) * 180) / Math.PI;
        // Normalize to [-180, 180) (true modulo) so the stored turn is the
        // small one AND always within the server's ±360 bound.
        const turn = normalizeTurnDeg(headingNeeded - last.headingDeg);
        updateActiveSection((section) => ({
            ...section,
            walls: [...section.walls, { uuid: newUuid(), lengthM: length, turnDeg: turn }],
        }));
    };

    // ------------------------------------------------------------------
    // Openings (doors / windows / passages on a wall)
    // ------------------------------------------------------------------
    const selectOpeningType = (type) => {
        const config = OPENING_TYPES.find((t) => t.value === type);
        setOpeningForm((prev) => ({
            ...prev,
            type,
            width: prev.width || (config.defaultWidthM * unitFactor).toFixed(1),
            height: prev.height || (config.defaultHeightM * unitFactor).toFixed(1),
            sill: type === 'window' ? (prev.sill || (DEFAULT_WINDOW_SILL_M * unitFactor).toFixed(1)) : '',
        }));
    };

    const addOpening = () => {
        setOpeningError(null);
        const wall = derived?.wallInfo.find((w) => w.uuid === openingForm.wallUuid);
        if (!wall) {
            setOpeningError('Pick the wall this goes on');
            return;
        }
        const widthM = toMeters(openingForm.width);
        const heightM = toMeters(openingForm.height);
        const offsetM = toMeters(openingForm.offset || 0);
        const sillM = openingForm.type === 'window' ? toMeters(openingForm.sill || 0) : 0;
        if (!Number.isFinite(widthM) || widthM <= 0 || !Number.isFinite(heightM) || heightM <= 0) {
            setOpeningError('Width and height must be greater than zero');
            return;
        }
        if (!Number.isFinite(offsetM) || offsetM < 0) {
            setOpeningError('Offset can\'t be negative');
            return;
        }
        if (offsetM + widthM > wall.lengthM + 1e-6) {
            setOpeningError(`Doesn't fit: wall is ${formatLinearMeasurement(wall.lengthM, unitSystem)}${suffix} long`);
            return;
        }
        updateActiveSection((section) => ({
            ...section,
            openings: [...section.openings, {
                uuid: newUuid(),
                type: openingForm.type,
                wallUuid: wall.uuid,
                offsetM,
                widthM,
                heightM,
                sillM,
            }],
        }));
        // Clear the wall pick along with the offset so the ghost preview
        // vanishes once the opening is real — otherwise a stale ghost would
        // sit at offset 0 right after every add.
        setOpeningForm((prev) => ({ ...prev, offset: '', wallUuid: '' }));
    };

    const removeOpening = (uuid) => {
        updateActiveSection((section) => ({
            ...section,
            openings: section.openings.filter((o) => o.uuid !== uuid),
        }));
    };

    // ------------------------------------------------------------------
    // Sections
    // ------------------------------------------------------------------
    const addSection = () => {
        // Start the new section's walk just past everything drawn so far,
        // so two rooms don't stack on top of each other at the origin. Must
        // account for BOTH parametric walls AND fixed (scan-imported)
        // surfaces — a scan_copy plan has no parametric walls, so ignoring
        // fixed geometry would start the new section at x=1, likely inside
        // the imported room.
        let maxX = 0;
        sections.forEach((section) => {
            const { wallInfo, data } = deriveSectionSurfaces(section);
            wallInfo.forEach((w) => {
                maxX = Math.max(maxX, w.start[0], w.end[0]);
            });
            // Fixed surfaces carry their world position in the transform's
            // translation column (index 12 = world X); extend the half-width
            // so the new origin clears the surface, not just its center.
            Object.values(data).forEach((surfaces) => {
                surfaces.forEach((s) => {
                    const tx = Array.isArray(s.transform) ? s.transform[12] : null;
                    if (Number.isFinite(tx)) {
                        const halfWidth = Array.isArray(s.dimensions) && Number.isFinite(s.dimensions[0])
                            ? Math.abs(s.dimensions[0]) / 2
                            : 0;
                        maxX = Math.max(maxX, tx + halfWidth);
                    }
                });
            });
        });
        setSections((prev) => [...prev, {
            sectionUuid: newUuid(),
            label: '',
            wallHeightM: activeSection?.wallHeightM || DEFAULT_WALL_HEIGHT_M,
            start: [maxX + 1, 0],
            startY: 0,
            frameRotationDeg: 0,
            walls: [],
            openings: [],
            fixedSurfaces: [],
            floorUuid: null,
        }]);
        setActiveSectionIdx(sections.length);
        setWallForm({ length: '', turn: 0 });
    };

    // ------------------------------------------------------------------
    // Save
    // ------------------------------------------------------------------
    const save = async () => {
        setSaving(true);
        try {
            // Rename + geometry in ONE request so a geometry validation
            // failure can't leave a half-applied rename persisted while the
            // UI reports the save failed.
            const trimmedName = planName.trim();
            const nameChanged = trimmedName && trimmedName !== plan.name;
            const updated = await UserFloorPlanService.saveSurfaces(
                plan.id,
                buildSavePayload(sections, nameChanged ? { name: trimmedName } : {}),
            );
            toast.success('Floor plan saved');
            onSaved(updated);
        } catch (err) {
            // Validation 400s carry field-level errors (formatError puts the
            // response's `errors` array on err.details). Show the first one —
            // a bare "Validation failed" gives no clue what to fix.
            const firstDetail = Array.isArray(err.details) ? err.details[0] : null;
            const detail = firstDetail?.msg
                ? ` — ${Array.isArray(firstDetail.loc) ? `${firstDetail.loc.join('.')}: ` : ''}${firstDetail.msg}`
                : '';
            toast.error(`${err.message || 'Failed to save floor plan'}${detail}`);
        } finally {
            setSaving(false);
        }
    };

    if (!activeSection) return null;

    const turnPresets = [
        { value: -90, label: 'Left', icon: CornerUpLeft },
        { value: 0, label: 'Straight', icon: MoveRight },
        { value: 90, label: 'Right', icon: CornerUpRight },
    ];

    return (
        <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-sm)] p-4 flex flex-col gap-4 overflow-y-auto no-scrollbar h-full" data-testid="floor-plan-editor">
            {/* Header: plan name + save/cancel */}
            <div className="flex items-center gap-2">
                <input
                    className={`${inputClass} font-bold`}
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    maxLength={128}
                    aria-label="Plan name"
                />
                <button
                    onClick={save}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-bold bg-[var(--primary)] text-[var(--primary-text)] hover:opacity-90 transition disabled:opacity-50"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Save
                </button>
                <button
                    onClick={onCancel}
                    disabled={saving}
                    className="px-3 py-2 rounded-xl text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition"
                >
                    Cancel
                </button>
            </div>

            {/* Section picker */}
            <div className="flex items-center gap-1.5 flex-wrap">
                {sections.map((section, idx) => (
                    <button
                        key={section.sectionUuid}
                        onClick={() => { setActiveSectionIdx(idx); setWallForm({ length: '', turn: section.walls.length ? 90 : 0 }); setEditingCell(null); }}
                        className={`px-2.5 py-1 rounded-full text-xs font-bold transition ${idx === activeSectionIdx
                            ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                            : 'bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--primary)]'
                        }`}
                    >
                        {section.label || `Section ${idx + 1}`}
                    </button>
                ))}
                <button
                    onClick={addSection}
                    className="px-2 py-1 rounded-full text-xs font-bold text-[var(--text-muted)] hover:text-[var(--primary)] transition"
                    aria-label="Add a section"
                >
                    <Plus size={14} />
                </button>
            </div>

            {/* Section meta */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <span className={labelClass}>Room label</span>
                    <input
                        className={inputClass}
                        value={activeSection.label}
                        placeholder="e.g. Bedroom"
                        maxLength={64}
                        onChange={(e) => updateActiveSection((s) => ({ ...s, label: e.target.value }))}
                    />
                </div>
                <div>
                    <span className={labelClass}>Wall height ({suffix})</span>
                    <input
                        className={inputClass}
                        type="number"
                        min="0"
                        step="any"
                        value={wallHeightInput ?? Number((activeSection.wallHeightM * unitFactor).toFixed(2))}
                        onChange={(e) => {
                            setWallHeightInput(e.target.value);
                            const meters = toMeters(e.target.value);
                            // Clamp to the server's (0, 15] bound so a tall
                            // value (e.g. 50 ft) can't preview but fail to save.
                            if (Number.isFinite(meters) && meters > 0) {
                                updateActiveSection((s) => ({ ...s, wallHeightM: Math.min(meters, MAX_WALL_HEIGHT_M) }));
                            }
                        }}
                        onBlur={() => setWallHeightInput(null)}
                        aria-label={`Wall height in ${unitSystem}`}
                    />
                </div>
            </div>

            {/* Walk start — translate the whole run into place. Arrows use
                top-view plan axes (up/down = -Z/+Z, left/right = -X/+X);
                the X/Z inputs are the precision fallback. The origin marker
                ghost in the viewer tracks every change live. */}
            <div data-testid="walk-start-controls">
                <div className="flex items-baseline justify-between">
                    <span className={labelClass}>Walk start</span>
                    <span className="text-[11px] text-[var(--text-muted)]">Moves the whole run</span>
                </div>
                <div className="mt-1 space-y-1.5">
                    {/* Step per arrow press (any decimal, e.g. 0.5) and the
                        run's rotation — wall 1's absolute heading, so the
                        whole chain pivots around the walk start. */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[11px] text-[var(--text-muted)]">Step</span>
                        <input
                            className={`${inputClass} !w-20`}
                            type="number"
                            min="0"
                            step="any"
                            value={nudgeStepInput}
                            onChange={(e) => setNudgeStepInput(e.target.value)}
                            aria-label={`Nudge step in ${unitSystem}`}
                            title={`How far each arrow press moves (${suffix})`}
                        />
                        <span className="text-[var(--text-muted)] text-xs">{suffix}</span>
                        <span className="text-[var(--text-muted)] ml-auto">
                            {turnArrow(frameRotationDeg)}
                        </span>
                        <input
                            className={`${inputClass} !w-16`}
                            type="number"
                            step="any"
                            value={runRotationValue()}
                            onChange={(e) => setRunRotation(e.target.value)}
                            onBlur={() => setPositionInput(null)}
                            aria-label="Run rotation in degrees"
                            title="Rotates the starter's X/Z axes to match the room — existing walls stay put; nudges and the first wall follow the rotated frame"
                        />
                        <span className="text-[var(--text-muted)]">°</span>
                    </div>
                    {/* One row per axis: [−] letter value [+]. Everything in
                        a row carries that axis's gizmo color via inline style
                        — 3D-scene colors, deliberately not theme tokens. */}
                    {NUDGE_AXES.map(({ axis, color, lineName }) => (
                        <div key={axis} className="flex items-center gap-1.5">
                            <button
                                onClick={() => nudge(axis, -1)}
                                className="p-1.5 rounded-lg bg-[var(--bg-hover)] hover:opacity-75 transition shrink-0"
                                style={{ color }}
                                aria-label={`Decrease walk start ${axis.toUpperCase()}`}
                                title={`− along the ${lineName}`}
                            >
                                <ArrowLeft size={14} />
                            </button>
                            <span
                                className="w-3 text-center text-[11px] font-bold shrink-0"
                                style={{ color }}
                                data-testid={`walk-start-${axis}-label`}
                            >
                                {axis.toUpperCase()}
                            </span>
                            <input
                                className={inputClass}
                                type="number"
                                step="any"
                                value={positionValue(axis)}
                                onChange={(e) => setStartAxis(axis, e.target.value)}
                                onBlur={() => setPositionInput(null)}
                                aria-label={`Walk start ${axis.toUpperCase()} in ${unitSystem}`}
                                title={`Walk start ${axis.toUpperCase()} (${suffix}) — the ${lineName}`}
                            />
                            <button
                                onClick={() => nudge(axis, 1)}
                                className="p-1.5 rounded-lg bg-[var(--bg-hover)] hover:opacity-75 transition shrink-0"
                                style={{ color }}
                                aria-label={`Increase walk start ${axis.toUpperCase()}`}
                                title={`+ along the ${lineName}`}
                            >
                                <ArrowRight size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Anchor card — appears when a scan-imported wall of this
                section is picked in the 3D viewer. Choosing an end + heading
                and applying moves the walk start onto that corner (and, for
                a fresh section, points the next wall along the preset). */}
            {anchorTarget && (
                <div data-testid="anchor-card" className="bg-[var(--bg-hover)] rounded-xl p-2.5 space-y-2">
                    <div className="flex items-center gap-1.5">
                        <Target size={13} className="text-[var(--primary)]" />
                        <span className={labelClass}>Anchor to scanned wall</span>
                    </div>
                    <p className="text-xs text-[var(--text-main)]">
                        Selected: {formatLinearMeasurement(anchorTarget.viewerDict.dimensions?.[0] ?? 0, unitSystem)}{suffix} wall
                    </p>
                    <div className="flex gap-1.5" role="group" aria-label="Which end to start at">
                        {[nearEndKey, nearEndKey === 'endA' ? 'endB' : 'endA'].map((endKey) => {
                            const isNear = endKey === nearEndKey;
                            const label = isNear ? 'Near end' : 'Far end';
                            return (
                                <button
                                    key={endKey}
                                    onClick={() => setAnchorChoice({ ...effectiveAnchorChoice, end: endKey })}
                                    className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-bold transition ${effectiveAnchorChoice.end === endKey
                                        ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                                        : 'bg-[var(--bg-card)] text-[var(--text-muted)] hover:text-[var(--primary)]'
                                    }`}
                                    aria-label={`Start at the ${label.toLowerCase()}`}
                                    aria-pressed={effectiveAnchorChoice.end === endKey}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    {activeSection.walls.length === 0 ? (
                        <div className="flex gap-1" role="group" aria-label="Heading relative to the wall">
                            {ANCHOR_PRESETS.map(({ value, label, ariaLabel, icon: Icon }) => (
                                <button
                                    key={value}
                                    onClick={() => setAnchorChoice({ ...effectiveAnchorChoice, preset: value })}
                                    className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-lg text-[11px] font-bold transition ${effectiveAnchorChoice.preset === value
                                        ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                                        : 'bg-[var(--bg-card)] text-[var(--text-muted)] hover:text-[var(--primary)]'
                                    }`}
                                    aria-label={ariaLabel}
                                    aria-pressed={effectiveAnchorChoice.preset === value}
                                >
                                    <Icon size={12} />
                                    {label}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="text-[11px] text-[var(--text-muted)]">
                            This section already has walls — this repositions the whole run.
                        </p>
                    )}
                    <button
                        onClick={applyAnchor}
                        className="w-full py-1.5 rounded-lg text-sm font-bold bg-[var(--primary)] text-[var(--primary-text)] hover:opacity-90 transition"
                    >
                        Use as start point
                    </button>
                </div>
            )}

            {/* Fixed-surface edit card — opens for any imported wall/door/
                window/opening picked in the list below or in the 3D view.
                Stacks with the anchor card when both apply to one wall. */}
            {editableFixedSurface && (
                <div
                    data-testid="fixed-surface-edit-card"
                    role="group"
                    aria-label={`Edit imported ${editableFixedSurface.surface_type}`}
                    className="bg-[var(--bg-hover)] rounded-xl p-2.5 space-y-2"
                >
                    <div className="flex items-center justify-between">
                        <span className={labelClass}>
                            <Pencil size={11} className="inline mr-1 align-[-1px]" />
                            Edit imported {editableFixedSurface.surface_type}
                        </span>
                        <button
                            onClick={() => requestDeleteFixedSurface(editableFixedSurface)}
                            className="text-[var(--text-muted)] hover:text-[var(--error)] transition"
                            aria-label={`Delete this ${editableFixedSurface.surface_type}`}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <span className="text-[11px] text-[var(--text-muted)]">Width ({suffix})</span>
                            <input
                                className={inputClass}
                                type="number"
                                min="0"
                                step="any"
                                value={fixedFieldValue('width')}
                                onChange={(e) => handleFixedFieldChange('width', e.target.value)}
                                onBlur={() => setFixedEditInput(null)}
                                aria-label={`Imported ${editableFixedSurface.surface_type} width in ${unitSystem}`}
                            />
                        </div>
                        <div>
                            <span className="text-[11px] text-[var(--text-muted)]">Height ({suffix})</span>
                            <input
                                className={inputClass}
                                type="number"
                                min="0"
                                step="any"
                                value={fixedFieldValue('height')}
                                onChange={(e) => handleFixedFieldChange('height', e.target.value)}
                                onBlur={() => setFixedEditInput(null)}
                                aria-label={`Imported ${editableFixedSurface.surface_type} height in ${unitSystem}`}
                            />
                        </div>
                    </div>
                    {editableFixedSurface.surface_type === 'window' && editableFixedParentWall && (
                        <div>
                            <span className="text-[11px] text-[var(--text-muted)]">Sill ({suffix})</span>
                            <input
                                className={inputClass}
                                type="number"
                                min="0"
                                step="any"
                                value={fixedFieldValue('sill')}
                                onChange={(e) => handleFixedFieldChange('sill', e.target.value)}
                                onBlur={() => setFixedEditInput(null)}
                                aria-label={`Imported window sill in ${unitSystem}`}
                            />
                        </div>
                    )}
                    {editableFixedSurface.surface_type === 'wall' && (
                        <>
                            <div>
                                <span className="text-[11px] text-[var(--text-muted)]">
                                    Depth ({suffix}) — standard if blank
                                </span>
                                <input
                                    className={inputClass}
                                    type="number"
                                    min="0"
                                    step="any"
                                    placeholder="Standard"
                                    value={fixedFieldValue('depth')}
                                    onChange={(e) => handleFixedFieldChange('depth', e.target.value)}
                                    onBlur={() => setFixedEditInput(null)}
                                    aria-label={`Imported wall depth in ${unitSystem}`}
                                />
                            </div>
                            <div>
                                <span className="text-[11px] text-[var(--text-muted)]">
                                    Resize from — the pinned end stays put
                                </span>
                                <div className="flex gap-1.5 mt-0.5" role="group" aria-label="Width resize anchor">
                                    {[
                                        { value: 'center', label: 'Center' },
                                        { value: 'endA', label: 'End A' },
                                        { value: 'endB', label: 'End B' },
                                    ].map(({ value, label }) => (
                                        <button
                                            key={value}
                                            onClick={() => setResizeAnchorChoice({ forId: selectedId, anchor: value })}
                                            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-bold transition ${effectiveResizeAnchor === value
                                                ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                                                : 'bg-[var(--bg-card)] text-[var(--text-muted)] hover:text-[var(--primary)]'
                                            }`}
                                            aria-label={value === 'center'
                                                ? 'Resize from the center'
                                                : `Pin ${label.toLowerCase()} while resizing`}
                                            aria-pressed={effectiveResizeAnchor === value}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                    {fixedFitWarnings.map((warning) => (
                        <p
                            key={`${warning.identifier}-${warning.axis}`}
                            data-testid="fixed-fit-warning"
                            className="text-xs text-[var(--error)]"
                        >
                            {/* Direction wording, not the dimension name: a
                                'height' overflow sticks out the top/bottom. */}
                            {`A door/window extends ${formatLinearMeasurement(warning.overflowM, unitSystem)}${suffix} past the wall's ${warning.axis === 'width' ? 'end' : 'top or bottom'} — enlarge the wall or delete the opening.`}
                        </p>
                    ))}
                </div>
            )}

            {/* Imported (scan-copied) surfaces — selectable rows, grouped by
                type. Selecting highlights the surface in the 3D view and
                opens the edit card above; floors stay view-only. */}
            {activeSection.fixedSurfaces.length > 0 && (
                <div data-testid="imported-surfaces-list">
                    <span className={labelClass}>Imported surfaces · from scan</span>
                    <div role="listbox" aria-label="Imported surfaces from the scan" className="space-y-1.5 mt-1">
                        {['wall', 'door', 'window', 'opening'].map((type) => {
                            const rows = activeSection.fixedSurfaces.filter((f) => f.surface_type === type);
                            if (rows.length === 0) return null;
                            return (
                                // role="group" + presentational <ul>: a
                                // listbox must own its options directly or
                                // through group/presentation elements, or
                                // assistive tech drops them.
                                <div key={type} role="group" aria-label={`${type}s`}>
                                    <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide px-1" aria-hidden="true">
                                        {type}s · {rows.length}
                                    </span>
                                    <ul className="space-y-1 mt-0.5" role="presentation">
                                        {rows.map((f, idx) => {
                                            const { identifier, dimensions } = f.viewerDict;
                                            const isRowSelected = selectedId === identifier;
                                            const sizeLabel = `${formatLinearMeasurement(Number(dimensions?.[0]) || 0, unitSystem)}×${formatLinearMeasurement(Number(dimensions?.[1]) || 0, unitSystem)}${suffix}`;
                                            return (
                                                <li
                                                    key={identifier}
                                                    role="option"
                                                    aria-selected={isRowSelected}
                                                    tabIndex={0}
                                                    onClick={() => onSelect?.(isRowSelected ? null : identifier)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            onSelect?.(isRowSelected ? null : identifier);
                                                        }
                                                    }}
                                                    className={`flex items-center justify-between text-sm rounded-lg px-2.5 py-1.5 cursor-pointer transition ${isRowSelected
                                                        ? 'bg-[var(--primary-bg)] ring-1 ring-[var(--primary)]'
                                                        : 'bg-[var(--bg-hover)] hover:opacity-80'
                                                    }`}
                                                >
                                                    <span className="text-[var(--text-main)] capitalize">
                                                        {type} {idx + 1}
                                                        <span className="text-[var(--text-muted)] ml-2 normal-case">{sizeLabel}</span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Wall list */}
            <div>
                <div className="flex items-baseline justify-between">
                    <span className={labelClass}>Walls · perimeter walk</span>
                    {derived?.closureErrorM != null && (
                        derived.isClosed ? (
                            <span className="text-xs font-bold text-[var(--secondary)]">Loop closed{derived.floorArea ? ` · ${formatArea(derived.floorArea, unitSystem)}` : ''}</span>
                        ) : (
                            <span className="text-xs text-[var(--text-muted)]">
                                Gap {formatLinearMeasurement(derived.closureErrorM, unitSystem)}{suffix}
                            </span>
                        )
                    )}
                </div>
                <ul className="mt-1 space-y-1">
                    {/* Every cell edits the parametric walk in place; walls
                        downstream of the edit re-chain automatically. Note an
                        edit inside a closed loop re-opens it (the gap
                        indicator returns) — Close Loop repairs that. */}
                    {activeSection.walls.map((wall, idx) => {
                        const hasHeightOverride = Number(wall.heightM) > 0;
                        return (
                        <li key={wall.uuid} className="text-sm bg-[var(--bg-hover)] rounded-lg px-2.5 py-1.5">
                            <div className="flex items-center gap-1.5">
                            <span className="text-[var(--text-muted)]">{idx + 1}</span>
                            <input
                                className={`${inputClass} !w-20 !bg-[var(--bg-card)]`}
                                type="number"
                                min="0"
                                step="any"
                                value={wallCellValue(wall, 'length')}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEditingCell({ uuid: wall.uuid, field: 'length', value });
                                    const meters = toMeters(value);
                                    if (Number.isFinite(meters) && meters > 0) {
                                        updateWall(wall.uuid, { lengthM: meters });
                                    }
                                }}
                                onBlur={() => setEditingCell(null)}
                                aria-label={`Wall ${idx + 1} length in ${unitSystem}`}
                            />
                            <span className="text-[var(--text-muted)]">{suffix}</span>
                            <span className="text-[var(--text-muted)] ml-auto">
                                {turnArrow(wall.turnDeg)}
                            </span>
                            <input
                                className={`${inputClass} !w-16 !bg-[var(--bg-card)]`}
                                type="number"
                                step="any"
                                value={wallCellValue(wall, 'turn')}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEditingCell({ uuid: wall.uuid, field: 'turn', value });
                                    const deg = Number(value);
                                    // Guard the empty string: Number('') === 0
                                    // would silently zero the turn mid-edit.
                                    // Clamp to the server's ±360 so a typed
                                    // value never previews-but-fails-to-save.
                                    if (value.trim() !== '' && Number.isFinite(deg)) {
                                        updateWall(wall.uuid, { turnDeg: clamp(deg, -MAX_TURN_DEG, MAX_TURN_DEG) });
                                    }
                                }}
                                onBlur={() => setEditingCell(null)}
                                aria-label={`Wall ${idx + 1} turn in degrees`}
                                title={idx === 0
                                    ? 'Absolute heading of the first wall, degrees'
                                    : 'Turn before this wall, degrees (+ right / − left)'}
                            />
                            <span className="text-[var(--text-muted)]">°</span>
                            <button
                                onClick={() => removeWall(wall.uuid)}
                                className="text-[var(--text-muted)] hover:text-[var(--error)] transition"
                                aria-label={`Remove wall ${idx + 1}`}
                            >
                                <Trash2 size={14} />
                            </button>
                            </div>
                            {/* Per-wall height: absent heightM = follows the
                                section height (muted, "default"); an override
                                sticks to this wall — pony walls, slope ends. */}
                            <div className="mt-1 flex items-center gap-1.5 pl-4">
                                <span className="text-[11px] text-[var(--text-muted)]">Height</span>
                                <input
                                    className={`${inputClass} !w-20 !bg-[var(--bg-card)] ${hasHeightOverride ? '' : '!text-[var(--text-muted)]'}`}
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={wallCellValue(wall, 'height')}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setEditingCell({ uuid: wall.uuid, field: 'height', value });
                                        if (value.trim() === '') return;
                                        const meters = toMeters(value);
                                        if (Number.isFinite(meters) && meters > 0) {
                                            updateWall(wall.uuid, { heightM: Math.min(meters, MAX_WALL_HEIGHT_M) });
                                        }
                                    }}
                                    onBlur={() => {
                                        setEditingCell(null);
                                        // Typing the section height back in IS
                                        // clearing the override — don't leave a
                                        // pinned copy that ignores section edits.
                                        if (wall.heightM != null
                                            && Math.abs(wall.heightM - activeSection.wallHeightM) <= HEIGHT_OVERRIDE_EPSILON_M) {
                                            updateWall(wall.uuid, { heightM: null });
                                        }
                                    }}
                                    aria-label={`Wall ${idx + 1} height in ${unitSystem}`}
                                />
                                <span className="text-[var(--text-muted)] text-xs">{suffix}</span>
                                {hasHeightOverride ? (
                                    <button
                                        onClick={() => updateWall(wall.uuid, { heightM: null })}
                                        className="flex items-center gap-1 text-[11px] text-[var(--text-muted)] hover:text-[var(--primary)] transition"
                                        aria-label={`Reset wall ${idx + 1} height to section default`}
                                    >
                                        <RotateCcw size={11} />
                                        Reset
                                    </button>
                                ) : (
                                    <span className="text-[11px] text-[var(--text-muted)]">(default)</span>
                                )}
                            </div>
                        </li>
                        );
                    })}
                    {activeSection.walls.length === 0 && (
                        <li className="text-xs text-[var(--text-muted)] px-1 py-1">
                            Enter your first wall&apos;s length below — each new wall continues from the last one&apos;s end.
                        </li>
                    )}
                </ul>
                {openingFitWarnings.map(({ opening, wallIdx }) => (
                    <p
                        key={`fit-${opening.uuid}`}
                        data-testid="opening-fit-warning"
                        className="mt-1 text-xs text-[var(--error)]"
                    >
                        {`The ${opening.type} on wall ${wallIdx + 1} no longer fits — lengthen the wall or move the ${opening.type}.`}
                    </p>
                ))}
            </div>

            {/* Add wall. The turn row shows for the FIRST wall too — its
                angle is the absolute heading from +X (same value the walk-
                start rotation field edits; they stay in sync because both
                write wallForm.turn while the section is empty). */}
            <div className="space-y-2">
                <div className="flex gap-1.5">
                    {turnPresets.map(({ value, label, icon: Icon }) => (
                        <button
                            key={value}
                            onClick={() => setWallForm((f) => ({ ...f, turn: value }))}
                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold transition ${Number(wallForm.turn) === value
                                ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                                : 'bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--primary)]'
                            }`}
                            aria-label={`Turn ${label.toLowerCase()}`}
                        >
                            <Icon size={13} />
                            {label}
                        </button>
                    ))}
                    <input
                        className={`${inputClass} !w-20`}
                        type="number"
                        step="any"
                        value={wallForm.turn}
                        onChange={(e) => setWallForm((f) => ({ ...f, turn: e.target.value }))}
                        aria-label="Turn angle in degrees"
                        title={activeSection.walls.length === 0
                            ? "Heading relative to the starter's X axis (0 = along X)"
                            : 'Turn before this wall, degrees (+ right / − left)'}
                    />
                </div>
                <div className="flex gap-1.5">
                    <input
                        className={inputClass}
                        type="number"
                        min="0"
                        step="any"
                        placeholder={`Wall length (${suffix})`}
                        value={wallForm.length}
                        onChange={(e) => setWallForm((f) => ({ ...f, length: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') addWall(); }}
                        aria-label={`Wall length in ${unitSystem}`}
                    />
                    <button
                        onClick={addWall}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold bg-[var(--primary)] text-[var(--primary-text)] hover:opacity-90 transition whitespace-nowrap"
                    >
                        <Plus size={14} />
                        Add Wall
                    </button>
                </div>
                {derived && derived.wallInfo.length >= 2 && !derived.isClosed && (
                    <button
                        onClick={closeLoop}
                        className="w-full py-1.5 rounded-lg text-xs font-bold bg-[var(--bg-hover)] text-[var(--primary)] hover:bg-[var(--primary-bg)] transition"
                    >
                        Close Loop (adds the final wall back to the start)
                    </button>
                )}
            </div>

            {/* Openings */}
            <div className="space-y-2">
                <span className={labelClass}>Doors · Windows · Openings</span>
                <ul className="space-y-1">
                    {activeSection.openings.map((opening) => {
                        const wallIdx = activeSection.walls.findIndex((w) => w.uuid === opening.wallUuid);
                        return (
                            <li key={opening.uuid} className="flex items-center justify-between text-sm bg-[var(--bg-hover)] rounded-lg px-2.5 py-1.5">
                                <span className="text-[var(--text-main)] capitalize">
                                    {opening.type}
                                    <span className="text-[var(--text-muted)] ml-2">
                                        wall {wallIdx + 1} · {formatLinearMeasurement(opening.widthM, unitSystem)}×{formatLinearMeasurement(opening.heightM, unitSystem)}{suffix}
                                    </span>
                                </span>
                                <button
                                    onClick={() => removeOpening(opening.uuid)}
                                    className="text-[var(--text-muted)] hover:text-[var(--error)] transition"
                                    aria-label={`Remove ${opening.type}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {activeSection.walls.length > 0 ? (
                    <div className="space-y-1.5">
                        <div className="flex gap-1.5">
                            <select
                                className={inputClass}
                                value={openingForm.type}
                                onChange={(e) => selectOpeningType(e.target.value)}
                                aria-label="Opening type"
                            >
                                {OPENING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <select
                                className={inputClass}
                                value={openingForm.wallUuid}
                                onChange={(e) => setOpeningForm((f) => ({ ...f, wallUuid: e.target.value }))}
                                aria-label="Wall to place it on"
                            >
                                <option value="">On wall…</option>
                                {activeSection.walls.map((wall, idx) => (
                                    <option key={wall.uuid} value={wall.uuid}>
                                        Wall {idx + 1} ({formatLinearMeasurement(wall.lengthM, unitSystem)}{suffix})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-1.5">
                            <input className={inputClass} type="number" min="0" step="any" placeholder={`Offset (${suffix})`} value={openingForm.offset} onChange={(e) => setOpeningForm((f) => ({ ...f, offset: e.target.value }))} aria-label="Offset from wall start" />
                            <input className={inputClass} type="number" min="0" step="any" placeholder={`Width (${suffix})`} value={openingForm.width} onChange={(e) => setOpeningForm((f) => ({ ...f, width: e.target.value }))} aria-label="Opening width" />
                            <input className={inputClass} type="number" min="0" step="any" placeholder={`Height (${suffix})`} value={openingForm.height} onChange={(e) => setOpeningForm((f) => ({ ...f, height: e.target.value }))} aria-label="Opening height" />
                            {openingForm.type === 'window' && (
                                <input className={inputClass} type="number" min="0" step="any" placeholder={`Sill (${suffix})`} value={openingForm.sill} onChange={(e) => setOpeningForm((f) => ({ ...f, sill: e.target.value }))} aria-label="Window sill height" />
                            )}
                        </div>
                        {openingError && <p className="text-xs text-[var(--error)]">{openingError}</p>}
                        <button
                            onClick={addOpening}
                            className="w-full py-1.5 rounded-lg text-xs font-bold bg-[var(--bg-hover)] text-[var(--primary)] hover:bg-[var(--primary-bg)] transition"
                        >
                            Add {OPENING_TYPES.find((t) => t.value === openingForm.type)?.label}
                        </button>
                    </div>
                ) : (
                    <p className="text-xs text-[var(--text-muted)] px-1">Add walls first, then place doors and windows on them.</p>
                )}
            </div>

            {/* Every imported-surface delete asks first. Copy says "until
                you Save" deliberately — unlike the plan-delete modal,
                nothing has hit the server yet and Cancel (the editor's)
                still restores everything. */}
            <ConfirmationModal
                isOpen={fixedSurfaceToDelete !== null}
                onClose={() => setFixedSurfaceToDelete(null)}
                onConfirm={confirmDeleteFixedSurface}
                title={`Delete this ${fixedSurfaceToDelete?.surfaceType ?? 'surface'}?`}
                message={(fixedSurfaceToDelete?.childCount ?? 0) > 0
                    ? `This wall has ${fixedSurfaceToDelete.childCount} door(s)/window(s)/opening(s) on it — deleting the wall removes them too. Nothing is saved until you press Save.`
                    : `This removes the imported ${fixedSurfaceToDelete?.surfaceType ?? 'surface'} from the plan. Nothing is saved until you press Save.`}
                confirmText="Delete"
                isDanger={true}
                icon={Trash2}
            />
        </div>
    );
};

export default FloorPlanEditorPanel;
